import { authFetch } from '../../auth/authFetch';
import { getApiBaseUrl } from '../../auth/api';
import type {
  ApiResponse,
  EmployerBankingDetailsDto,
  SaveEmployerBankingRequestDto,
  SaveNominatedDeductionRequestDto,
  SaveSpCreditorRequestDto,
  UpdateNominatedDeductionRequestDto,
  UpdateSpCreditorRequestDto,
} from './organisationApiTypes';

const base = (businessRecordId: string) =>
  `${getApiBaseUrl()}/api/business-records/${encodeURIComponent(businessRecordId)}/employer-banking`;

const parse = async (response: Response): Promise<EmployerBankingDetailsDto> => {
  // The body is read even on an error status, because business refusals carry
  // their operator-facing message in the JSON envelope. An unhandled server
  // error is not JSON at all — ASP.NET returns a plain-text exception page —
  // and parsing it used to throw first, hiding the status behind a JSON syntax
  // error. Fall back to the status instead.
  const body = await response.json().catch(() => null) as ApiResponse<EmployerBankingDetailsDto> | null;
  if (!response.ok) throw new Error(body?.message || `Request failed: ${response.status}`);
  if (!body?.success || body.result == null)
    throw new Error(body?.message || 'The server returned an unreadable Employer banking response.');
  return body.result;
};

const json = (method: 'PUT' | 'POST', body: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

export const getEmployerBanking = async (businessRecordId: string): Promise<EmployerBankingDetailsDto> =>
  parse(await authFetch(base(businessRecordId)));

export const saveEmployerBanking = async (
  businessRecordId: string,
  request: SaveEmployerBankingRequestDto,
): Promise<EmployerBankingDetailsDto> =>
  parse(await authFetch(base(businessRecordId), json('PUT', request)));

export const addSpCreditor = async (
  businessRecordId: string,
  request: SaveSpCreditorRequestDto,
): Promise<EmployerBankingDetailsDto> =>
  parse(await authFetch(`${base(businessRecordId)}/sp-creditors`, json('POST', request)));

export const updateSpCreditor = async (
  businessRecordId: string,
  designationId: string,
  request: UpdateSpCreditorRequestDto,
): Promise<EmployerBankingDetailsDto> =>
  parse(await authFetch(`${base(businessRecordId)}/sp-creditors/${encodeURIComponent(designationId)}`, json('PUT', request)));

export const addNominatedDeduction = async (
  businessRecordId: string,
  request: SaveNominatedDeductionRequestDto,
): Promise<EmployerBankingDetailsDto> =>
  parse(await authFetch(`${base(businessRecordId)}/nominated-deductions`, json('POST', request)));

export const updateNominatedDeduction = async (
  businessRecordId: string,
  nominationId: string,
  request: UpdateNominatedDeductionRequestDto,
): Promise<EmployerBankingDetailsDto> =>
  parse(await authFetch(`${base(businessRecordId)}/nominated-deductions/${encodeURIComponent(nominationId)}`, json('PUT', request)));
